import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Volume2, VolumeX, Play, Check, AlertCircle,
  Smartphone, Monitor, Bell, Settings
} from 'lucide-react';
import {
  NOTIFICATION_SOUNDS,
  getCurrentSound,
  setNotificationSound,
  getVolume,
  setVolume,
  isSoundEnabled,
  setSoundEnabled,
  previewSound,
  stopPreview,
  getAudioSettingsInstructions
} from '@/lib/audioSettings';

export function AudioSettingsPanel({ compact = false }) {
  const [selectedSound, setSelectedSound] = useState(getCurrentSound().id);
  const [volume, setVolumeState] = useState(getVolume());
  const [enabled, setEnabled] = useState(isSoundEnabled());
  const [playing, setPlaying] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const instructions = getAudioSettingsInstructions();
  
  const volumeArray = useMemo(() => [volume], [volume]);

  const handleSoundChange = (soundId) => {
    setSelectedSound(soundId);
    setNotificationSound(soundId);
  };

  const handleVolumeChange = (values) => {
    const newVolume = values[0];
    setVolumeState(newVolume);
    setVolume(newVolume);
  };

  const handleEnabledChange = (value) => {
    setEnabled(value);
    setSoundEnabled(value);
  };

  const handlePreview = async (soundId) => {
    if (playing === soundId) {
      stopPreview();
      setPlaying(null);
    } else {
      setPlaying(soundId);
      await previewSound(soundId);
      setTimeout(() => setPlaying(null), 1500);
    }
  };

  useEffect(() => {
    return () => stopPreview();
  }, []);

  if (compact) {
    return (
      <div className="space-y-4" data-testid="audio-settings-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {enabled ? <Volume2 className="h-4 w-4 text-emerald-600" /> : <VolumeX className="h-4 w-4 text-slate-400" />}
            <Label htmlFor="sound-enabled">Notification Sound</Label>
          </div>
          <Switch
            id="sound-enabled"
            checked={enabled}
            onCheckedChange={handleEnabledChange}
            data-testid="sound-toggle"
          />
        </div>

        {enabled && (
          <>
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Sound</Label>
              <div className="flex flex-wrap gap-2">
                {NOTIFICATION_SOUNDS.map((sound) => (
                  <Button
                    key={sound.id}
                    variant={selectedSound === sound.id ? "default" : "outline"}
                    size="sm"
                    className={selectedSound === sound.id ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    onClick={() => {
                      handleSoundChange(sound.id);
                      handlePreview(sound.id);
                    }}
                    data-testid={`sound-option-${sound.id}`}
                  >
                    {playing === sound.id ? (
                      <Volume2 className="h-3 w-3 mr-1 animate-pulse" />
                    ) : selectedSound === sound.id ? (
                      <Check className="h-3 w-3 mr-1" />
                    ) : null}
                    {sound.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Volume: {volume}%</Label>
              <Slider
                value={volumeArray}
                onValueChange={handleVolumeChange}
                max={100}
                step={5}
                className="w-full"
                data-testid="volume-slider"
              />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Card data-testid="audio-settings-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Bell className="h-5 w-5 text-emerald-600" />
          Notification Sound Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            {enabled ? <Volume2 className="h-5 w-5 text-emerald-600" /> : <VolumeX className="h-5 w-5 text-slate-400" />}
            <div>
              <Label htmlFor="sound-enabled-full" className="font-medium">Sound Alerts</Label>
              <p className="text-sm text-slate-500">Play a sound when new positions are available</p>
            </div>
          </div>
          <Switch
            id="sound-enabled-full"
            checked={enabled}
            onCheckedChange={handleEnabledChange}
          />
        </div>

        {enabled && (
          <>
            {/* Sound Selection */}
            <div className="space-y-3">
              <Label className="font-medium">Choose Alert Sound</Label>
              <p className="text-sm text-slate-500">Select a distinctive sound so you never miss a position</p>
              <div className="grid gap-2">
                {NOTIFICATION_SOUNDS.map((sound) => (
                  <div
                    key={sound.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedSound === sound.id 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleSoundChange(sound.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedSound === sound.id ? 'border-emerald-500' : 'border-slate-300'
                      }`}>
                        {selectedSound === sound.id && (
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{sound.name}</p>
                        <p className="text-sm text-slate-500">{sound.description}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(sound.id);
                      }}
                      data-testid={`preview-sound-${sound.id}`}
                    >
                      {playing === sound.id ? (
                        <Volume2 className="h-4 w-4 animate-pulse text-emerald-600" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Volume Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Volume</Label>
                <Badge variant="secondary">{volume}%</Badge>
              </div>
              <Slider
                value={volumeArray}
                onValueChange={handleVolumeChange}
                max={100}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>Quiet</span>
                <span>Loud</span>
              </div>
            </div>
          </>
        )}

        {/* Platform-specific Instructions */}
        <div className="border-t pt-4">
          <Button
            variant="ghost"
            className="w-full justify-between"
            onClick={() => setShowInstructions(!showInstructions)}
          >
            <span className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Device Sound Settings Guide
            </span>
            <AlertCircle className="h-4 w-4 text-slate-400" />
          </Button>

          {showInstructions && (
            <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-900 flex items-center gap-2 mb-3">
                {instructions.platform === 'iOS' ? <Smartphone className="h-4 w-4" /> : 
                 instructions.platform === 'Android' ? <Smartphone className="h-4 w-4" /> : 
                 <Monitor className="h-4 w-4" />}
                {instructions.title}
              </h4>
              <ol className="space-y-2">
                {instructions.steps.map((step, index) => (
                  <li key={step.title} className="text-sm">
                    <span className="font-medium text-amber-900">{index + 1}. {step.title}:</span>
                    <span className="text-amber-800 ml-1">{step.detail}</span>
                  </li>
                ))}
              </ol>
              {instructions.tip && (
                <p className="mt-3 text-sm text-amber-700 italic">
                  💡 {instructions.tip}
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default AudioSettingsPanel;
